import { useQuery, useQueryClient } from '@tanstack/react-query';
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { useMemo, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';

import { AllocateMachineFromSiteDialog } from '@/components/sites/AllocateMachineFromSiteDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { formatMachineName } from '@/lib/utils/machine-label';
import { listMachinesRequest } from '@/services/machines';
import type { MachineListItem } from '@/types/machines';
import type { PaginatedResult } from '@/types/roles';

/**
 * Plan 11-03 — SiteMachinesTab
 *
 * Closes Phase 9 SITE-105 by replacing the placeholder Alert with a live,
 * gated machines table sourced from `GET /api/machines?siteId={id}&status=active`
 * (Plan 11-01). The "Allocate Machine" button (gated by `machines.update`)
 * launches `AllocateMachineFromSiteDialog`; on a successful allocation the
 * site-scoped query is invalidated and the new row appears immediately.
 *
 * data-testid contract:
 *   - site-machines-tab          (root, with data-site-id)
 *   - site-machines-loading      (loading state)
 *   - site-machines-error        (error state)
 *   - site-machines-empty        (empty state)
 *   - site-machines-table        (TanStack Table)
 *   - site-machines-row-name-{id} (per-row Name link)
 *   - site-machines-allocate-button (only when canAllocate)
 */
export interface SiteMachinesTabProps {
  readonly siteId: number;
}

export function SiteMachinesTab({ siteId }: SiteMachinesTabProps): JSX.Element {
  const { permissions } = useAuth();
  const canAllocate = permissions.includes('machines.update');
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  const machinesQuery = useQuery<PaginatedResult<MachineListItem>, ApiError>({
    queryKey: ['machines', { siteId, status: 'active' as const }],
    queryFn: () => listMachinesRequest({ siteId, status: 'active', pageSize: 100 }),
    enabled: Number.isFinite(siteId) && siteId > 0,
    staleTime: 30_000,
  });

  const columns = useMemo<ColumnDef<MachineListItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <Link
            to={`/machines/${String(row.original.id)}`}
            className="text-primary hover:underline"
            data-testid={`site-machines-row-name-${String(row.original.id)}`}
          >
            {formatMachineName(row.original.name, row.original.vehicleNumber)}
          </Link>
        ),
      },
      {
        accessorKey: 'code',
        header: 'Code',
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.code}</span>,
      },
      {
        accessorKey: 'machineTypeName',
        header: 'Type',
        cell: ({ row }) => <span>{row.original.machineTypeName}</span>,
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={row.original.isActive ? 'default' : 'secondary'}>
            {row.original.isActive ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
    ],
    [],
  );

  const data = machinesQuery.data?.items ?? [];
  const tableData = data as unknown as MachineListItem[];

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-3" data-testid="site-machines-tab" data-site-id={siteId}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Machines allocated to this site</h3>
          <p className="text-xs text-muted-foreground">
            Live roster from /api/machines?siteId={String(siteId)}&amp;status=active. Click a name
            to drill into the machine.
          </p>
        </div>
        {canAllocate && (
          <Button
            type="button"
            onClick={() => {
              setDialogOpen(true);
            }}
            data-testid="site-machines-allocate-button"
          >
            Allocate Machine
          </Button>
        )}
      </div>

      {machinesQuery.isLoading && (
        <p className="text-sm text-muted-foreground" data-testid="site-machines-loading">
          Loading machines…
        </p>
      )}
      {machinesQuery.isError && (
        <p className="text-sm text-destructive" data-testid="site-machines-error">
          Failed to load machines.
        </p>
      )}
      {machinesQuery.data && (
        <Table data-testid="site-machines-table">
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((h) => (
                  <TableHead key={h.id}>
                    {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center py-8">
                  <span data-testid="site-machines-empty">
                    No machines allocated to this site yet.
                  </span>
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-testid={`site-machines-row-${String(row.original.id)}`}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      <AllocateMachineFromSiteDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        siteId={siteId}
        onSuccess={() => {
          void queryClient.invalidateQueries({
            queryKey: ['machines', { siteId, status: 'active' }],
          });
          // Invalidate the broader machines cache so the unallocated dropdown
          // and any list view (MachinesListPage) refetches in lockstep.
          void queryClient.invalidateQueries({ queryKey: ['machines'] });
        }}
      />
    </div>
  );
}
